import CloseIcon from "@mui/icons-material/Close";
import { Backup } from "@mui/icons-material";
import {
  Button,
  CircularProgress,
  Dialog,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { ChangeEvent, FormEvent, useState } from "react";
import {
  ActionByType,
  ProfileDocument,
  ProfileDocumentType,
  ProfileDocumentTypeLabel,
} from "../../common/ProfileDocument";
import { useUploadProfileDocumentMutation } from "../../redux/api/profileDocument";
import { toast } from "react-toastify";

interface ProfileDocumentsUploadModalProps {
  profileId: number;
  onUpload: Function;
  actionByType: ActionByType;
}

const ProfileDocumentsUploadModal = (
  props: ProfileDocumentsUploadModalProps
) => {
  const [profileDocument, setProfileDocument] = useState<ProfileDocument>({
    id: 0,
    fileUrl: "",
    fileKey: "",
    contentType: "",
    type: ProfileDocumentType.MMC, // default to MMC
    issueDate: "",
    expirationDate: "",
    displayName: "",
    actionByType: props.actionByType,
  });
  const [file, setFile] = useState<File>();
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [uploadProfileDocument] = useUploadProfileDocumentMutation();

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  // method to reset form to defaults
  const clearForm = () => {
    setProfileDocument({
      id: 0,
      fileUrl: "",
      fileKey: "",
      contentType: "",
      type: ProfileDocumentType.MMC, // default to MMC
      issueDate: "",
      expirationDate: "",
      displayName: "",
      actionByType: props.actionByType,
    });
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const mb = 1024 * 1024; // size of a MB in Bytes
    if (!event.target.files || !event.target.files[0]) {
      setFile(undefined);
      return;
    }
    if (event.target.files[0].size > 20 * mb) {
      toast.error(<p>File is too big. Files must be 20MB or less.</p>, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
      return;
    }
    setFile(event.target.files[0]);
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    event.stopPropagation();
    // check that required values are present
    if (!file) {
      toast.error(<p>No valid file to Upload.</p>, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
      return;
    }

    uploadDocument(file, convertProfileDocumentToBody(profileDocument));
  };

  const uploadDocument = async (file: File, body: string) => {
    setUploading(true);
    // format file and body as Form Data
    const data = new FormData();
    data.append("file", file);
    data.append("document", new Blob([body], { type: "application/json" }));
    const response = await uploadProfileDocument(data);
    setUploading(false);
    if ("error" in response === false) {
      handleClose();
      clearForm();
      props.onUpload();
    }
  };

  const convertProfileDocumentToBody = (document: ProfileDocument) => {
    // convert empty values to undefined
    return JSON.stringify({
      profile: {
        id: props.profileId,
      },
      type: document.type,
      issueDate: document.issueDate ? document.issueDate : undefined,
      expirationDate: document.expirationDate
        ? document.expirationDate
        : undefined,
      actionByType: document.actionByType,
    });
  };

  return (
    <>
      <Button
        className="blue-link-button"
        id="profile-upload"
        onClick={handleOpen}
      >
        <Backup />
        Upload Document
      </Button>
      <Dialog
        className="modal"
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-title"
      >
        <div className="modal-header mb-2">
          <Typography id="modal-title" variant="h3">
            Upload Document
          </Typography>
          <Button onClick={() => setOpen(false)} className="grey-link-button">
            <CloseIcon />
          </Button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-narrow" id="upload-fields">
            <div>
              <InputLabel id="typeLabel">Document Type</InputLabel>
              <Select
                labelId="typeLabel"
                id="type"
                notched={false}
                value={profileDocument.type}
                label="Document Type"
                onChange={(event) => {
                  if (
                    (event.target.value as ProfileDocumentType) ===
                    ProfileDocumentType.COVID_VACCINE_CARD
                  ) {
                    setProfileDocument({
                      ...profileDocument,
                      type: event.target.value as ProfileDocumentType,
                      expirationDate: "",
                    });
                  } else {
                    setProfileDocument({
                      ...profileDocument,
                      type: event.target.value as ProfileDocumentType,
                    });
                  }
                }}
              >
                {Object.keys(ProfileDocumentType)
                  .filter((type) => type !== ProfileDocumentType.COVID_VACCINE_CARD &&
                    type !== ProfileDocumentType.TRAINING)
                  .map((type) => (
                    <MenuItem key={type} value={type as ProfileDocumentType}>
                      {ProfileDocumentTypeLabel[type as ProfileDocumentType]}
                    </MenuItem>
                  ))}
              </Select>
            </div>
            <label htmlFor="issueDate">
              <span>Issue Date:</span>
              <input
                type="date"
                id="issueDate"
                value={profileDocument.issueDate}
                onChange={(event) =>
                  setProfileDocument({
                    ...profileDocument,
                    issueDate: event.target.value,
                  })
                }
              />
            </label>
            <label htmlFor="expirationDate">
              <span>Expiration Date:</span>
              <input
                type="date"
                id="expirationDate"
                value={profileDocument.expirationDate}
                onChange={(event) =>
                  setProfileDocument({
                    ...profileDocument,
                    expirationDate: event.target.value,
                  })
                }
              />
            </label>
          </div>
          <label htmlFor="fileUpload">
            <span>Upload File:</span>
            <input type="file" id="fileUpload" onChange={handleFileChange} />
          </label>
          <div className="modal-button">
            <Button
              className="outline-button-small"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <input
              className="blue-button-small"
              type="submit"
              value="Upload Document"
              disabled={uploading}
            />
            {uploading && <CircularProgress />}
          </div>
        </form>
      </Dialog>
    </>
  );
};

export default ProfileDocumentsUploadModal;
